/**
 * useAuth — minimalistický auth state z localStorage.
 *
 * Source of truth:
 *   - localStorage["bw:token"]       → access JWT
 *   - localStorage["bw_token"]       → zpětně-kompatibilní alias (čte api-client)
 *   - localStorage["bw:refreshToken"] → refresh token (zatím se nepoužívá automaticky)
 *   - localStorage["bw:userId"]      → ID auth účtu (po B-refactoru player→user)
 *   - localStorage["bw:role"]        → role: viewer | player | bartender | admin
 *
 * Reaguje na změny v jiných záložkách přes `storage` event + vlastní `bw:auth-change`
 * event (LoginPage / logout ho odpálí, aby header v té samé záložce hned překreslil).
 */
import { useCallback, useEffect, useState } from "react";

export type Role = "viewer" | "player" | "bartender" | "admin";

const ROLE_RANK: Record<Role, number> = { viewer: 0, player: 1, bartender: 2, admin: 3 };

/** True pokud aktuální role je `min` nebo vyšší. */
export function hasRole(role: Role | null, min: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
  userId: string | null;
  role: Role | null;
}

function readAuth(): AuthState {
  if (typeof window === "undefined") {
    return { isLoggedIn: false, token: null, userId: null, role: null };
  }
  const token = localStorage.getItem("bw:token") ?? localStorage.getItem("bw_token");
  const userId = localStorage.getItem("bw:userId") ?? localStorage.getItem("bw:playerId");
  const rawRole = localStorage.getItem("bw:role");
  const role: Role | null =
    rawRole === "viewer" || rawRole === "player" || rawRole === "bartender" || rawRole === "admin"
      ? rawRole
      : null;
  return { isLoggedIn: Boolean(token), token, userId, role };
}

/**
 * Obnoví access token přes uložený refresh token (POST /auth/refresh, rotuje
 * refresh token). Vrací nový access token, nebo null pokud refresh token chybí /
 * expiroval / byl revokován. Aktualizuje localStorage + odpálí `bw:auth-change`.
 * Souběžné volání se deduplikují (WS reconnect + REST 401 najednou).
 */
let refreshInFlight: Promise<string | null> | null = null;
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const refreshToken = localStorage.getItem("bw:refreshToken");
      if (!refreshToken) return null;
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { token: string; refreshToken: string };
      localStorage.setItem("bw:token", data.token);
      localStorage.setItem("bw_token", data.token);
      localStorage.setItem("bw:refreshToken", data.refreshToken);
      window.dispatchEvent(new Event("bw:auth-change"));
      return data.token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Sjednocené smazání všech auth klíčů + odpálení change eventu. */
export function clearStoredAuth(): void {
  try {
    localStorage.removeItem("bw:token");
    localStorage.removeItem("bw_token");
    localStorage.removeItem("bw:refreshToken");
    localStorage.removeItem("bw:userId");
    localStorage.removeItem("bw:playerId");
    localStorage.removeItem("bw:role");
  } catch {
    // private mode / disabled storage
  }
  window.dispatchEvent(new Event("bw:auth-change"));
}

const NO_REDIRECT_PATHS = ["/login", "/signup"];

/**
 * Session vypršela a NELZE ji obnovit (refresh token chybí/expiroval) → vyčisti
 * auth stav a hard-redirect na `/login?next=<aktuální cesta>`. Volá api-client
 * (REST 401 po neúspěšném refreshi) i useWs (WS 1008 po neúspěšném refreshi).
 * Na /login a /signup neredirectuje (tam je 401 přirozený).
 */
export function handleSessionExpired(): void {
  clearStoredAuth();
  if (typeof window === "undefined") return;
  if (NO_REDIRECT_PATHS.includes(window.location.pathname)) return;
  const next = window.location.pathname + window.location.search;
  window.location.replace(`/login?next=${encodeURIComponent(next)}`);
}

/** Vrátí cílovou cestu pro redirect po loginu dle role.
 *  Admin/bartender jde na landing `/admin`, ne přímo na live — odtud má přehled
 *  všech sekcí (sezóny, hráči, theme editor, scoreboard config, …) a může si
 *  vybrat. Live dashboard je jen jedna z nich. */
export function defaultLandingForRole(role: Role | null): string {
  if (role === "admin" || role === "bartender") return "/admin";
  return "/me";
}

export function useAuth(): AuthState & { logout: () => Promise<void> } {
  const [state, setState] = useState<AuthState>(readAuth);

  useEffect(() => {
    function refresh() {
      setState(readAuth());
    }
    window.addEventListener("storage", refresh); // jiná záložka
    window.addEventListener("bw:auth-change", refresh); // ten samý dokument
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("bw:auth-change", refresh);
    };
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem("bw:refreshToken");
    // Best-effort revokace na backendu — pokud selže (network, 401), klient
    // stejně smaže lokální stav (frontend stop platí, byť backend ještě ne).
    if (refreshToken) {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // ignoruj
      }
    }
    clearStoredAuth();
  }, []);

  return { ...state, logout };
}
